import type { UTCDate } from '@date-fns/utc';
import { isBefore } from 'date-fns';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../shared/user-preferences/types';
import { deriveEffectiveTaxonomiesForScope } from '../../../../shared/user-preferences/utils/derive-effective-taxonomies';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import type { CustomActivityTaxonomySelection } from '../definitions/activityTaxonomy';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import {
  type DateRangeFilter,
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  type MultiFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';
import { getActivityDefaultSelectionsForTaxonomy } from '../utils/getActivityDefaultSelectionsForTaxonomy';
import { getRoleDefaultSelectionsForTaxonomy } from '../utils/getRoleDefaultSelectionsForTaxonomy';
import { getSkillDefaultSelectionsForTaxonomy } from '../utils/getSkillDefaultSelectionsForTaxonomy';
import { pickFirstWhenMatchingPreset } from '../utils/primaryFilterUtils';

const normalizeRoleTaxonomySelection = (
  selection:
    | CustomRoleTaxonomySelection
    | { default: true }
    | 'adaptive'
    | undefined
): CustomRoleTaxonomySelection => {
  if (!selection || selection === 'adaptive') return { default: true };
  return selection as CustomRoleTaxonomySelection;
};

const areRoleTaxonomiesEquivalent = (
  a: CustomRoleTaxonomySelection,
  b: CustomRoleTaxonomySelection
): boolean => {
  if ('default' in a && a.default) {
    return 'default' in b && b.default === true;
  }
  if ('default' in b && b.default) return false;

  if ('taxonomyId' in a && typeof a.taxonomyId !== 'undefined') {
    return (
      'taxonomyId' in b &&
      typeof b.taxonomyId !== 'undefined' &&
      Number(b.taxonomyId) === Number(a.taxonomyId)
    );
  }

  if ('ricsK50Id' in a && a.ricsK50Id) {
    return 'ricsK50Id' in b && b.ricsK50Id === a.ricsK50Id;
  }

  if ('ricsK10Id' in a && a.ricsK10Id) {
    return 'ricsK10Id' in b && b.ricsK10Id === a.ricsK10Id;
  }

  return false;
};

type FilterTransformer = (
  triggerFilter: Filter,
  currentDependentFilterState: Filter | undefined,
  fullFilterMap: FilterSelectionsMap
) => Partial<Filter> | null;

export interface FilterDependency {
  dependentFilterId: FilterId;
  transformer: FilterTransformer;
}

export const FILTER_DEPENDENCIES: Map<FilterId, FilterDependency[]> = new Map([
  [
    FilterId.SNAPSHOT_DATE,
    [
      {
        dependentFilterId: FilterId.DATE_RANGE,
        transformer: (triggerFilter, dependentFilter) => {
          const snapshotDate = (
            triggerFilter as SingleValueFilter<UTCDate | null>
          )?.value;
          const dateRangeFilter = dependentFilter as DateRangeFilter;

          if (snapshotDate === null) {
            return {
              ...dateRangeFilter,
              id: FilterId.DATE_RANGE,
              min: null,
              max: null,
            };
          }

          if (
            dateRangeFilter.max === null ||
            isBefore(snapshotDate, dateRangeFilter.max)
          ) {
            return {
              ...dateRangeFilter,
              id: FilterId.DATE_RANGE,
              min: snapshotDate,
              max: dateRangeFilter.max,
            };
          }

          return {
            ...dateRangeFilter,
            id: FilterId.DATE_RANGE,
            min: dateRangeFilter.min,
            max: null,
          };
        },
      },
    ],
  ],
  [
    FilterId.DATE_RANGE,
    [
      {
        dependentFilterId: FilterId.SNAPSHOT_DATE,
        transformer: (triggerFilter, dependentFilter) => {
          const dateRangeFilter = triggerFilter as DateRangeFilter;
          const snapshotDateFilter =
            dependentFilter as SingleValueFilter<UTCDate | null>;

          return {
            ...snapshotDateFilter,
            id: FilterId.SNAPSHOT_DATE,
            value: dateRangeFilter.min,
          };
        },
      },
    ],
  ],
  [
    FilterId.ROLE_TAXONOMY,
    [
      {
        dependentFilterId: FilterId.ROLE,
        transformer: (triggerFilter, currentDependentFilterState) => {
          const taxonomyFilter =
            triggerFilter as SingleValueFilter<CustomRoleTaxonomySelection>;
          const roleFilter = currentDependentFilterState as
            | TreeFilter
            | undefined;

          // When ROLE is primary (role page): apply taxonomy defaults
          if (roleFilter?.isPrimary) {
            const rolePrefs = PreferenceBrowserStorage.get(
              PreferenceScope.ROLE
            );
            const storedDefaults =
              rolePrefs[UserPreferenceKey.DefaultFilters] ?? [];
            // Taxonomy choice is GLOBAL (derived: adaptive → generic on role pages).
            const globalTaxonomies = deriveEffectiveTaxonomiesForScope(
              PreferenceBrowserStorage.get(PreferenceScope.GLOBAL)[
                UserPreferenceKey.Taxonomies
              ],
              PreferenceScope.ROLE
            );
            const storedTaxonomyPref = normalizeRoleTaxonomySelection(
              globalTaxonomies?.role
            );
            const currentTaxonomy = normalizeRoleTaxonomySelection(
              taxonomyFilter.value
            );

            // A version bump on the same taxonomy must not reuse stale stored
            // defaults. The current version rides on the taxonomy filter value;
            // the stored stamp is what the saved defaults were captured under.
            const storedVersion = globalTaxonomies?.roleTaxonomyVersion;
            const currentVersion = taxonomyFilter.value?.version;

            const shouldUseStoredDefaults =
              storedDefaults.length > 0 &&
              areRoleTaxonomiesEquivalent(
                storedTaxonomyPref,
                currentTaxonomy
              ) &&
              (!currentVersion || storedVersion === currentVersion);

            // Seed the way a reload does: one entity from the taxonomy's
            // defaults, saved defaults in full unless they are that preset.
            const taxonomyDefaults = getRoleDefaultSelectionsForTaxonomy(
              taxonomyFilter.value
            );
            const primarySelections = shouldUseStoredDefaults
              ? pickFirstWhenMatchingPreset(storedDefaults, [taxonomyDefaults])
              : taxonomyDefaults.slice(0, 1);

            const update: Partial<TreeFilter> = {
              id: FilterId.ROLE,
              selections: primarySelections,
              isUserSubmitted: false,
              primarySelections,
            };

            return update;
          }

          // When ROLE is secondary (non-role pages): a genuine taxonomy VALUE
          // change invalidates standing role selections — role ids are
          // taxonomy-relative, so a selection kept across the change silently
          // repoints at a different role. Clear them, except on a paired
          // (`isAiApplied`) trigger, whose ids were resolved under the
          // incoming value by construction.
          if (
            roleFilter &&
            roleFilter.selections.length > 0 &&
            taxonomyFilter.isAiApplied !== true
          ) {
            return {
              id: FilterId.ROLE,
              selections: [],
              isUserSubmitted: false,
            };
          }

          return null;
        },
      },
      {
        dependentFilterId: FilterId.MULTI_ROLE,
        transformer: (triggerFilter, currentDependentFilterState) => {
          const multiRoleFilter = currentDependentFilterState as
            | MultiFilter<TreeFilter>
            | undefined;

          // Same paired-trigger exemption as the ROLE transformer above.
          if (
            multiRoleFilter?.instances &&
            multiRoleFilter.instances.length > 0 &&
            triggerFilter.isAiApplied !== true
          ) {
            return {
              id: FilterId.MULTI_ROLE,
              instances: [],
              isUserSubmitted: false,
            };
          }

          return null;
        },
      },
    ],
  ],
  [
    FilterId.SKILL_TAXONOMY,
    [
      {
        dependentFilterId: FilterId.CUSTOM_SKILL,
        transformer: (triggerFilter, currentDependentFilterState) => {
          const taxonomyFilter =
            triggerFilter as SingleValueFilter<CustomSkillTaxonomySelection>;
          const skillFilter = currentDependentFilterState as
            | TreeFilter
            | undefined;

          // When CUSTOM_SKILL is primary (skill page): apply the taxonomy's
          // defaults. Saved SKILL-scope defaults win only when captured under
          // the same taxonomy AND version.
          if (skillFilter?.isPrimary) {
            const skillPrefs = PreferenceBrowserStorage.get(
              PreferenceScope.SKILL
            );
            const storedDefaults =
              skillPrefs[UserPreferenceKey.DefaultFilters] ?? [];
            const globalTaxonomies = deriveEffectiveTaxonomiesForScope(
              PreferenceBrowserStorage.get(PreferenceScope.GLOBAL)[
                UserPreferenceKey.Taxonomies
              ],
              PreferenceScope.SKILL
            );
            const storedTaxonomyId =
              typeof globalTaxonomies?.skill === 'number'
                ? globalTaxonomies.skill
                : 0;
            const currentTaxonomyId = taxonomyFilter.value?.taxonomyId ?? 0;

            const storedVersion = globalTaxonomies?.skillTaxonomyVersion;
            const currentVersion = taxonomyFilter.value?.version;

            const shouldUseStoredDefaults =
              storedDefaults.length > 0 &&
              storedTaxonomyId === currentTaxonomyId &&
              (!currentVersion || storedVersion === currentVersion);

            // Seed the way a reload does: one entity from the taxonomy's
            // defaults, saved defaults in full unless they are that preset.
            const taxonomyDefaults =
              getSkillDefaultSelectionsForTaxonomy(currentTaxonomyId);
            const primarySelections = shouldUseStoredDefaults
              ? pickFirstWhenMatchingPreset(storedDefaults, [taxonomyDefaults])
              : taxonomyDefaults.slice(0, 1);

            const update: Partial<TreeFilter> = {
              id: FilterId.CUSTOM_SKILL,
              selections: primarySelections,
              isUserSubmitted: false,
              primarySelections,
            };

            return update;
          }

          // When CUSTOM_SKILL is secondary: a taxonomy value change clears
          // the selections (skill ids are taxonomy-relative), except on a
          // paired (`isAiApplied`) trigger, whose ids were resolved under the
          // incoming value by construction.
          if (
            currentDependentFilterState &&
            'selections' in currentDependentFilterState &&
            currentDependentFilterState.selections.length > 0 &&
            triggerFilter.isAiApplied !== true
          ) {
            return {
              id: FilterId.CUSTOM_SKILL,
              selections: [],
              isUserSubmitted: false,
            };
          }
          return null;
        },
      },
      {
        dependentFilterId: FilterId.MULTI_CUSTOM_SKILL,
        transformer: (triggerFilter, currentDependentFilterState) => {
          // Same paired-trigger exemption as CUSTOM_SKILL above.
          if (
            currentDependentFilterState &&
            'instances' in currentDependentFilterState &&
            currentDependentFilterState.instances.length > 0 &&
            triggerFilter.isAiApplied !== true
          ) {
            return {
              id: FilterId.MULTI_CUSTOM_SKILL,
              instances: [],
              isUserSubmitted: false,
            };
          }

          return null;
        },
      },
    ],
  ],
  [
    FilterId.ACTIVITY_TAXONOMY,
    [
      {
        dependentFilterId: FilterId.ACTIVITY,
        transformer: (triggerFilter, currentDependentFilterState) => {
          const taxonomyFilter =
            triggerFilter as SingleValueFilter<CustomActivityTaxonomySelection>;
          const activityFilter = currentDependentFilterState as
            | TreeFilter
            | undefined;

          // When ACTIVITY is primary (activity page): apply the taxonomy's
          // defaults. Saved ACTIVITY-scope defaults win only when captured
          // under the same taxonomy AND version.
          if (activityFilter?.isPrimary) {
            const activityPrefs = PreferenceBrowserStorage.get(
              PreferenceScope.ACTIVITY
            );
            const storedDefaults =
              activityPrefs[UserPreferenceKey.DefaultFilters] ?? [];
            const globalTaxonomies = deriveEffectiveTaxonomiesForScope(
              PreferenceBrowserStorage.get(PreferenceScope.GLOBAL)[
                UserPreferenceKey.Taxonomies
              ],
              PreferenceScope.ACTIVITY
            );
            const storedTaxonomyId =
              typeof globalTaxonomies?.activity === 'number'
                ? globalTaxonomies.activity
                : 0;
            const currentTaxonomyId = taxonomyFilter.value?.taxonomyId ?? 0;

            const storedVersion = globalTaxonomies?.activityTaxonomyVersion;
            const currentVersion = taxonomyFilter.value?.version;

            const shouldUseStoredDefaults =
              storedDefaults.length > 0 &&
              storedTaxonomyId === currentTaxonomyId &&
              (!currentVersion || storedVersion === currentVersion);

            // Seed the way a reload does: one entity from the taxonomy's
            // defaults, saved defaults in full unless they are that preset.
            const taxonomyDefaults =
              getActivityDefaultSelectionsForTaxonomy(currentTaxonomyId);
            const primarySelections = shouldUseStoredDefaults
              ? pickFirstWhenMatchingPreset(storedDefaults, [taxonomyDefaults])
              : taxonomyDefaults.slice(0, 1);

            const update: Partial<TreeFilter> = {
              id: FilterId.ACTIVITY,
              selections: primarySelections,
              isUserSubmitted: false,
              primarySelections,
            };

            return update;
          }

          // When ACTIVITY is secondary: a taxonomy value change clears
          // taxonomy-relative selections (unchanged behaviour).
          if (
            activityFilter &&
            'selections' in activityFilter &&
            activityFilter.selections.length > 0 &&
            triggerFilter.isAiApplied !== true
          ) {
            return {
              id: FilterId.ACTIVITY,
              selections: [],
              isUserSubmitted: false,
            };
          }
          return null;
        },
      },
      {
        dependentFilterId: FilterId.MULTI_ACTIVITY,
        transformer: (triggerFilter, currentDependentFilterState) => {
          // Same paired-trigger exemption as ACTIVITY above: activity ids are
          // taxonomy-relative, so a taxonomy change invalidates every instance.
          if (
            currentDependentFilterState &&
            'instances' in currentDependentFilterState &&
            currentDependentFilterState.instances.length > 0 &&
            triggerFilter.isAiApplied !== true
          ) {
            return {
              id: FilterId.MULTI_ACTIVITY,
              instances: [],
              isUserSubmitted: false,
            };
          }
          return null;
        },
      },
    ],
  ],
]);
